/**
 * successStoriesData.ts
 *
 * Single source of truth for:
 *   • /success-stories         → listing card  (SuccessStorySingle)
 *   • /success-stories/:id     → full story   (SuccessStoryCard)
 *
 * The route components match against `story_id`, so keep those slugs in sync
 * with the folder names under /public/success_stories/.
 */

/* ─────────────────────────────  Types  ───────────────────────────── */

export type ChallengeItem = {
  title: string;
  description: string;
};

export type DesignStep = {
  number: number;
  title: string;
  /** Optional badge, e.g. "made by revolv space" / "made by c3s" */
  credit?: string;
  bullets: string[];
};

export type ArchitectureItem = {
  number: number;
  title: string;
  description: string;
  image: string;
  imageLabel: string;
  /**
   * Visual treatment of the image frame.
   *   • true / omitted → product render: gradient backdrop + object-contain (HOLLY, PAVEL…)
   *   • false          → real photograph: full-bleed object-cover, no gradient backdrop
   */
  isProduct?: boolean;
  /** Optional credit shown under the frame, e.g. "image: u-space" (auto-uppercased). */
  credit?: string;
  /**
   * Product page slug (e.g. "holly", "pavel", "sara", "mara").
   * When present, renders a gradient "DISCOVER {SLUG}" button under the description,
   * linking to /products/{slug}/. Only set on product items.
   */
  productSlug?: string;
};

export type ImpactRow = {
  metric: string;
  results: string;
  value: string;
};

export type MilestoneRow = {
  milestone: string;
  number: string;
};

export type TimelineRow = {
  milestone: string;
  date: string;
};

export type Quote = {
  text: string;
  author: string;
};

export type SuccessStory = {
  story_id: string;
  slug: string;
  themeColor: string;


  /* ── Listing card (SuccessStorySingle) ── */
  /** Short uppercase teaser. Supports \n for line breaks. */
  card_description: string;

  /* ── Detail page header ── */
  title: string;
  subtitle?: string;
  intro: string;
  client: {
    name: string;
    logo: string;
    tagline: string;
    highlightAliases?: string[];

  };
  subtitleHighlight: string;
  descriptionHighlight: string;
  heroImage: string;
  heroImageCaption?: string;

  /* Mission */
  mission: {
    name: string;
    subtitle: string;
    paragraphs: string[];
    ctaText: string;
    ctaLink: string;
  };

  /* Challenge */
  challenge: {
    heading: string;
    description: string;
    items: ChallengeItem[];
  };

  wideImage: string;
  wideImageCaption?: string;

  /* Design / Qualification steps */
  design: {
    heading: string;
    subheading: string;
    steps: DesignStep[];
  };

  /* Architecture */
  architecture: {
    heading: string;
    subheading: string;
    description: string;
    items: ArchitectureItem[];
  };

  /* Impact table */
  impact: {
    heading: string;
    rows: ImpactRow[];
  };

  /* Legacy / scale */
  legacy: {
    heading: string;
    description: string;
    sideText: string;
    milestones: MilestoneRow[];
  };

  /* Timeline */
  timeline: {
    heading: string;
    rows: TimelineRow[];
  };

  /* Closing imagery + quotes */
  endImage: string;
  endImageCaption?: string;
  quotes: Quote[];

  /* Final CTA */
  cta: {
    heading: string;
    description: string;
    buttonText: string;
    buttonLink: string;
  };
};

/* ─────────────────────────────  Data  ───────────────────────────── */

export const SUCCESS_STORIES: SuccessStory[] = [
  /* ═══════════════════════ C3S / MAUVE ═══════════════════════ */
  {
    story_id: "c3s-mauve",
    slug: "c3s-mauve",
    themeColor:"#03848F" ,

    card_description: "POWER GENERATION\nFOR UV ASTRONOMY",

    title:
        "Power Generation Subsystem Design and Production for an Astronomy Mission",
    intro:
        "C3S selected Revolv Space as the power generation subsystem supplier for the MAUVE mission — a 16U CubeSat performing high-resolution UV spectrophotometry in the 200–700 nm range, requiring an exceptionally stable mechanical environment to protect sensitive scientific data throughout a five-year mission in LEO.",
    client: {
      name: "C3S LLC",
      logo: "/success_stories/c3s-mauve/c3s_logo.png",
      tagline: "A european reference in science-grade CubeSat platforms.",
        highlightAliases: ["C3S", "c3s", "C3S LLC"]
    },
    subtitleHighlight: "",
    descriptionHighlight: "",

    heroImage: "/success_stories/c3s-mauve/C3S_SS_BIG.png",
    heroImageCaption: "mauve satellite. Credits: C3S",

    mission: {
      name: "MAUVE",
      subtitle:
          "A mission dedicated to high-resolution UV spectrophotometry of stellar activity and exoplanet atmospheres.",
      paragraphs: [
        "MAUVE is a 16U CubeSat developed by C3S, designed to study stellar flares — sudden, intense bursts of radiation from active stars — and their effects on the atmospheres of orbiting exoplanets. Operating in the 200–700 nm UV-visible range, the mission performs high-frequency spectral sampling that requires the satellite to hold precise pointing across multi-minute observation windows.",
      ],
      ctaText: "DISCOVER MORE ABOUT C3S",
      ctaLink: "https://c3s.hu",
    },

    challenge: {
      heading:
          "MAIN CHALLENGE: STABLE POWER WITHOUT DISTURBING THE OBSERVATION",
      description:
          "The MAUVE spectrometer is intolerant of mechanical disturbance. Delivering sufficient power without introducing vibration into the platform created three simultaneous constraints:",
      items: [
        {
          title: "STRUCTURAL JITTER",
          description:
              "Any structural resonance in the deployed solar array within the ADCS control bandwidth introduces micro-vibrations during pointing manoeuvres — blurring stellar spectra and rendering observation data unusable.",
        },
        {
          title: "EXTRA POWER",
          description:
              "A standard body-mounted configuration would limit observations to short bursts, potentially missing unpredictable stellar flare events. The mission required deployable wings capable of guaranteeing at least a ~50% duty cycle.",
        },
        {
          title: "PROCUREMENT SPEED",
          description:
              "The European Commission's funding schedule fixed the launch window. From the solar array contract signature in January 2024, flight hardware had to be delivered in Q3 2024. Standard solar cell procurement alone took six months.",
        },
      ],
    },

    wideImage: "/success_stories/c3s-mauve/C3S_SS_SECOND.png",
    wideImageCaption: "pavel and holly fm",

    design: {
      heading: "REVOLV SPACE",
      subheading: "DESIGN AND C3S QUALIFICATION",
      steps: [
        {
          number: 1,
          title: "Power Subsystem Design & Analysis",
          credit: "",
          bullets: [
            "Three solar arrays designed from scratch: 2 deployable wings + 1 body-mounted panel",
            "Modal and structural analysis: deployed natural frequency >5 Hz",
            "ADCS decoupling verification: array resonance above control bandwidth",
            "Motorisation margin analysis for worst-case thermal deployment",
            "Structural Model (STM) campaign: bus interface validation and deployment testing",
          ],
        },
        {
          number: 2,
          title: "Acceptance Testing",
          credit: "made by revolv space",
          bullets: [
            "Deployment functional testing at assembly level",
            "Full documentation package: ICD, structural and power reports (BOL/EOL), user manual",
            "3 flight units delivered to C3S, Q3 2024",
          ],
        },
        {
          number: 3,
          title: "Satellite-Level Acceptance Campaign",
          credit: "made by c3s",
          bullets: [
            "Incoming inspection and subsystem electrical checks",
            "TVAC: non-op extremes, cold switch-ON, operational thermal cycling — all requirements met",
            "Random vibration: all 3 axes, per SpaceX launch provider requirements",
            "Coupling tests: solar array & EPS, CSS, ADCS",
            "All MAUVE functionalities confirmed intact",
          ],
        },
      ],
    },

    architecture: {
      heading: "REVOLV SPACE",
      subheading:"16U SINGLE-DEPLOYABLE ARCHITECTURE",
      description: "We designed and delivered a single-deployable 16U solar wing.",
      items: [
        {
          number: 1,
          title: "Deployment Mechanism",
          description:
              "Proprietary HOLLY hinges ensuring a natural frequency >5 Hz in deployed configuration — decoupling array resonance from the ADCS control bandwidth and preventing vibration from reaching the UV sensor.",
          image: "/success_stories/c3s-mauve/RECTANGLE-6.png",
          imageLabel: "HOLLY",
          productSlug: "holly",
        },
        {
          number: 2,
          title: "Solar Arrays",
          description:
              "8U aluminium panels with our proprietary flexible printed circuit (FPC) technology, guaranteeing lower manufacturing times, scalable high-volume production and higher reliability.",
          image: "/success_stories/c3s-mauve/PAVEL.png",
          imageLabel: "PAVEL",
          productSlug: "pavel",
        },
      ],
    },

    impact: {
      heading: "THE IMPACT OF REVOLV SPACE",
      rows: [
        {
          metric: "Observation continuity",
          results: "~24W BOL orbit average power",
          value: 'Enables long-duration stellar "staring" modes',
        },
        {
          metric: "Data integrity",
          results: ">5 Hz deployed stiffness",
          value: "Vibration-free platform for UV spectrophotometry",
        },
        {
          metric: "Operational duty cycle",
          results: "~50% duty cycle enabled",
          value: "Sustained high-frequency sampling and autonomous processing",
        },
      ],
    },

    legacy: {
      heading: "FROM A SINGLE MISSION TO A SCIENTIFIC STANDARD",

      description:
          "The design and delivery model established for MAUVE — science-grade structural performance, accepted and satellite-tested to SpaceX launch requirements — is now validated for European scientific CubeSat programmes.",
      sideText: "",
      milestones: [
        { milestone: "HOLLY flight units delivered", number: "4" },
        { milestone: "PAVEL flight units delivered", number: "3" },
        { milestone: "Currently flying", number: "All" },
      ],
    },

    timeline: {
      heading: "PROGRAMME TIMELINE",
      rows: [
        { milestone: "Contract signed", date: "January 2024" },
        { milestone: "Acceptance testing complete", date: "Q3 2024" },
        { milestone: "Flight hardware delivered", date: "Q3 2024" },
        { milestone: "Mission launch", date: "November 2025" },
      ],
    },

    endImage: "/success_stories/c3s-mauve/C3S_SS_END.jpg",
    endImageCaption:
        "From left to right: Filippo Oggionni, Chief Commercial Officer, Revolv Space | Péter Koncsár, Chief Project Officer, C3S | Gyula Horváth, Chief Executive Officer, C3S",

    quotes: [
      {
        text: "This collaboration demonstrates the strength of European space partnerships and the importance of reliable, high-quality industrial cooperation in space missions. The joint effort supports a mission that will significantly contribute to the study of stellar activity and exoplanetary environments.",
        author: "Gyula Horváth, Chief Executive Officer of C3S LLC",
      },
      {
        text: "This collaboration with C3S has been a great success for Revolv Space. Supporting the MAUVE mission allowed us to demonstrate our ability to design, engineer and validate high-performance power generation subsystems and mechanisms in record time for such a prestigious mission.",
        author: "Filippo Oggionni, Chief Commercial Officer, Revolv Space",
      },
    ],

    cta: {
      heading: "Ready to Power Your Next Mission?",
      description:
          "Don't let stability constraints compromise your payload's science. Join us building precision scientific and Earth observation missions who trust Revolv Space for stable, flight-proven power generation systems.",
      buttonText: "Book a discovery call",
      buttonLink: "/contact",
    },
  },

  /* ══════════════════ U-SPACE / SOAP & PANDORE ══════════════════ */
  {
    story_id: "u-space",
    slug: "u-space",
    themeColor: "#2D6B8B",

    card_description: "POWER GENERATION\nFOR SSA & LEO-PNT",

    title: "High-Performance Power Generation Subsystems Design for U-Space's 12U CubeSat Missions",
    intro:
        "U-Space selected Revolv Space to design, qualify, and deliver the power generation subsystem for its 12U CubeSat missions — each with distinct payload architectures, all demanding the same uncompromising flight standard.",
    client: {
      name: "U-Space",
      logo: "/success_stories/u-space/U-Space-logo.png",
      tagline: "An emerging leader in next-generation CubeSat missions.",
      highlightAliases: ["U-SPACE", "u-space", "U-Space"]
    },
    subtitleHighlight: "— news, april 2026",
    descriptionHighlight: "Revolv Space will support u-space for\n" + "the future 16u cubesats",
    heroImage: "/success_stories/u-space/U-SPACE_SS_BIG.jpg",
    heroImageCaption: "u-space team during integration. Credits: u-space",

    mission: {
      name: "Two missions dedicated to space based situational awareness and positioning navigation timing in low earth orbit (LEO-PNT) launched last year, and a third one to come",
      subtitle: "",
      paragraphs: [
        "The first is a Space Situational Awareness mission designed to optically track and characterise debris in low Earth orbit. Any oscillation blurs the image and renders debris tracking data unusable.",
        "The second is a European first: the inaugural in-orbit demonstration of LEO-PNT (Low-Earth-Orbit Positioning, Navigation and Timing) technology which paves the way for a new generation of constellations able to augment and secure today's GNSS systems such as GPS or Galileo.",
      ],
      ctaText: "DISCOVER MORE ABOUT U-SPACE",
      ctaLink: "https://www.u-space.fr/en/\n",
    },

    challenge: {
      heading:
          "MAIN CHALLENGE: HIGH POWER WITHOUT COMPROMISING ON PLATFORM STABILITY",
      description:
          "The mission requirement for high-resolution debris tracking and high-speed X-band data downlink created a significant technical challenge:",
      items: [
        {
          title: "OAP DEFICIT",
          description:
              "Body-mounted panels would provide <30W Orbit Average Power, while the high-power payloads provided by Airbus and Microtec required sustained duty cycles.",
        },
        {
          title: "LOW JITTER",
          description:
              "Typical deployable arrays are not stiff enough, introducing low-frequency oscillations that render sensitive optical sensors inoperable.",
        },
        {
          title: "VOLUME ENVELOPE",
          description:
              "The entire double-deployable system had to fit within the Exolaunch 12U deployer.",
        },
      ],
    },

    wideImage: "/success_stories/u-space/U-SPACE_SS_SECOND.jpg",
    wideImageCaption: "image: u-space",

    design: {
      heading: "REVOLV SPACE",
      subheading:"DESIGN AND QUALIFICATION",
      steps: [
        {
          number: 1,
          title: "Solar Wing Requirements Analysis & Design",
          credit:"Including ECSS-compatible motorisation margin analysis for deployment",

          bullets: [
            "Wing architecture design (double-deployable)",
            "Structural and thermal analyses",
            "Motorisation margin analysis — cold-case stiction verified",
          ],
        },
        {
          number: 2,
          title: "Qualification",
          credit: "Full multi-model qualification campaign",
          bullets: [
            "Structural model: assembly, integration and deployment tests in ambient conditions with offloading equipment",
            "Qualification model: assembly, integration and functional testing",
            "Structural test: sine vibration, random vibration",
            "TVAC: 8 cycles — deployment performed in cold case",
            "Fatigue: 50 deployment cycles in ambient conditions",
          ],
        },
        {
          number: 3,
          title: "High-Volume Production",
          credit: "42 flight models delivered",
          bullets: [
            "Set up serial production line",
            "HOLLY units assembled and acceptance tested, then integrated with solar wing assembly",
          ],
        },
      ],
    },

    architecture: {
      heading: "REVOLV SPACE",
      subheading: "DOUBLE-DEPLOYABLE ARCHITECTURE",
      description:
          "We designed and qualified a double deployable, high-stiffness 12U solar wing.",
      items: [
        {
          number: 1,
          title: "Deployment Mechanism",
          description:
              "Proprietary HOLLY hinges based on ECSS-compliant design, ensuring reliable deployment under worst-case cold stiction conditions.",
          image: "/success_stories/u-space/holly1.png",
          imageLabel: "HOLLY",
          productSlug: "holly",
        },
        {
          number: 2,
          title: "Structural Substrates",
          description:
              "Aluminium panels, hard-anodized and engineered for high modal frequency to eliminate mechanical jitter.",
          image: "/success_stories/u-space/RECTANGLE-7.png",
          imageLabel: "HOLLY / SUBSTRATE",
          isProduct: false,
        },
        {
          number: 3,
          title: "Photovoltaic Assembly (PVA)",
          description:
              "High-efficiency solar cell strings (PVA) manufactured and bonded by a partner.",
          image: "/success_stories/u-space/RECTANGLE-5.png",
          imageLabel: "12U CUBESATS",
          isProduct: false,
          credit: "image: u-space",
        },
      ],
    },

    impact: {
      heading: "THE IMPACT OF REVOLV SPACE",
      rows: [
        {
          metric: "Orbit average power",
          results: "3x increase",
          value: "Enabled continuous payload operations",
        },
        {
          metric: "Structural rigidity",
          results: "High natural frequency",
          value: "Zero mechanical jitter; clear image capture",
        },
        {
          metric: "Deployment risk mitigation",
          results: "Deployment in TVAC validated",
          value: "Maximized probability of mission success",
        },
      ],
    },

    legacy: {
      heading: "FROM QUALIFICATION TO CONSTELLATION SCALE",
      description:
          "Qualifying a solar wing for a single mission is a significant investment. Building the production infrastructure to support a LEO constellation is a different challenge entirely — and one that Revolv Space is looking to address.",
      sideText: "The design and qualification foundation established for U-Space 12U first products has since been scaled into a repeatable production capability:",
      milestones: [
        { milestone: "Units produced for internal development", number: "10" },
        { milestone: "Flight units delivered (total)", number: "42" },
        { milestone: "Currently flying", number: "12" },
      ],
    },

    timeline: {
      heading: "PROGRAM TIMELINE",
      rows: [
        { milestone: "Contract signed", date: "September 2023" },
        { milestone: "Critical design review", date: "January 2024" },
        { milestone: "Qualification campaign", date: "March – June 2024" },
        { milestone: "FM — 18 holly units delivered", date: "August 2024" },
      ],
    },

    endImage: "/success_stories/u-space/U-SPACE_SS_END.png",

    quotes: [
      {
        text: "Revolv delivered on what they promised. Their synergy with our manufacturing partner and their proactivity during the design and qualification phase were key factors for our selection and brought us to mission success.",
        author: "Cyril Brotons, CTO & VP Industrial Strategy, U-Space",
      },
      {
        text: "The success of U-Space two first 12U missions is a collective achievement, and we are thrilled to have played a vital role in bringing these important missions to fruition. Our ability to deliver high-quality, flight-proven hardware on an accelerated timeline and to support the high-volume needs of our customers positions Revolv Space as a key enabler for the future of smallsat LEO constellations.",
        author: "Filippo Oggionni, Chief Commercial Officer, Revolv Space",
      },
    ],

    cta: {
      heading: "Ready to Power Your Next Mission?",
      description:
          "Don't let power constraints limit your payload's potential. Join the leaders in SSA and Earth Observation who trust Revolv Space for flight-proven power generation systems.",
      buttonText: "Book a discovery call",
      buttonLink: "/contact",
    },
  },
];
